import { useCallback, useRef, useState } from 'react'
import { useAuth } from '../utils/Auth/AuthProvider'
import { useGeminiClient } from './useGeminiClient'
import { request } from '../utils/remote'

const stripHtml = (html: string) => html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()

export type QuestionStatus = 'pending' | 'chapter_done' | 'rag_done' | 'explanation_done' | 'failed' | 'no_context'
export type FailedPhase = 'chapters' | 'rag' | 'explanation'

type RunOptions = {
    courseIds: string[]
    examId: string | null
    gradeId?: string
    /** Grades whose chapters may be assigned (the chosen books' grades); overrides gradeId. */
    gradeIds?: string[]
    subjectId?: string
    useOptions: boolean
    allRags: any[]
    selectedRagIds: string[]
}

export interface PipelineQuestion {
    // Original question fields
    [key: string]: any
    // Pipeline tracking
    _status: QuestionStatus
    _retries: number
    _course_id?: number | null
    _ragHits?: any[]
    _error?: string
    /** The step a failed question stopped at, so "Retry failed" restarts it there. */
    _failedPhase?: FailedPhase
    _processing?: boolean
    _chapterAssignment?: {
        chaporsubid: number
        is_subchapter: boolean
        chapter_id: number
        chapter_name: string
        subchapter_name?: string
        course_id?: number | null
    }
}

export type PipelinePhase = 'idle' | 'detecting' | 'chapters' | 'rag' | 'explanation' | 'done' | 'error'

export interface PipelineProgress {
    phase: PipelinePhase
    total: number
    completed: number
    failed: number
    message: string
}

export interface PipelineResults {
    succeeded: number[]
    failed: number[]
    noContext: number[]
}

const MAX_RETRIES = 3
const CHAPTER_BATCH_SIZE = 15
const EXPLANATION_BATCH_SIZE = 5

export function usePipelineEngine(geminiKey: string | null) {
    const { token } = useAuth()
    const gemini = useGeminiClient(geminiKey)
    const [questions, setQuestions] = useState<PipelineQuestion[]>([])
    const [progress, setProgress] = useState<PipelineProgress>({ phase: 'idle', total: 0, completed: 0, failed: 0, message: '' })
    const [results, setResults] = useState<PipelineResults>({ succeeded: [], failed: [], noContext: [] })
    const cancelRef = useRef(false)
    const questionsRef = useRef<PipelineQuestion[]>([])
    // Options of the last full run, so "Retry failed" can re-run chapter assignment.
    const lastRunOptsRef = useRef<RunOptions | null>(null)
    // The last chapter catalog fetched: the choices for a manual chapter change after processing.
    const [chapterCatalog, setChapterCatalog] = useState<any[]>([])

    const updateQuestion = useCallback((idx: number, patch: Partial<PipelineQuestion>) => {
        // Update ref immediately (synchronous) so retry loops see fresh data
        const copy = [...questionsRef.current]
        copy[idx] = { ...copy[idx], ...patch }
        questionsRef.current = copy
        setQuestions(copy)
    }, [])

    const updateQuestions = useCallback((patchFn: (prev: PipelineQuestion[]) => PipelineQuestion[]) => {
        // Compute from current ref immediately (synchronous) so retry loops see fresh data
        const next = patchFn(questionsRef.current)
        questionsRef.current = next
        setQuestions(next)
    }, [])

    const setQuestionsCustom = useCallback((qs: PipelineQuestion[]) => {
        setQuestions(qs)
        questionsRef.current = qs
    }, [])

    // Phase A: Detect chapter type
    const detectChapterType = useCallback(async (courseIds: string[]): Promise<'chapter_option' | 'chapter' | null> => {
        let hasOptions = false
        let hasChapters = false
        for (const cid of courseIds) {
            try {
                const { getCourseChapterOptions } = await import('../utils/remote')
                const data = await getCourseChapterOptions(token, Number(cid))
                if (data?.chapters?.length > 0) hasOptions = true
                else hasChapters = true
            } catch {
                hasChapters = true
            }
        }
        if (hasOptions && !hasChapters) return 'chapter_option'
        if (hasChapters && !hasOptions) return 'chapter'
        return null // ambiguous — caller should ask user
    }, [token])

    // Phase B: Chapter assignment with auto-retry
    const assignChapters = useCallback(async (
        qs: PipelineQuestion[],
        courseIds: string[],
        useOptions: boolean,
        examId: string | null,
        gradeId?: string | string[],
        subjectId?: string
    ) => {
        setProgress({ phase: 'chapters', total: qs.length, completed: 0, failed: 0, message: 'Fetching chapter catalog...' })

        // Fetch catalog
        const catalogPayload = {
            use_options: useOptions,
            course_ids: courseIds,
            grade_ids: Array.isArray(gradeId) ? gradeId : gradeId ? [gradeId] : [],
            subject_id: subjectId
        }
        const catalogResp = await request(`/api/school/exam-detail/${examId || ''}/assign-chapters-ai/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
            body: JSON.stringify(catalogPayload)
        })
        const catalog = catalogResp?.catalog || []
        if (!catalog.length) throw new Error('No chapters found for the given criteria')
        setChapterCatalog(catalog)

        // Build parent/sub maps
        const parentChapters = catalog.filter((c: any) => !c.is_subchapter)
        const subchapterMap: Record<string, any[]> = {}
        parentChapters.forEach((ch: any) => {
            subchapterMap[String(ch.id)] = catalog.filter((s: any) => s.is_subchapter && String(s.chapter_id) === String(ch.id))
        })

        let completed = 0
        let failed = 0
        const processedIndices = new Set<number>()

        for (let retry = 0; retry < MAX_RETRIES; retry++) {
            if (cancelRef.current) return
            const pending = questionsRef.current
                .map((q, i) => ({ q, i }))
                .filter(({ q, i }) => q._status === 'pending' && !processedIndices.has(i))

            if (pending.length === 0) break

            setProgress(p => ({ ...p, message: retry > 0 ? `Retry ${retry}/${MAX_RETRIES} for ${pending.length} questions...` : `Assigning chapters...` }))

            for (let b = 0; b < pending.length; b += CHAPTER_BATCH_SIZE) {
                if (cancelRef.current) return
                const batch = pending.slice(b, b + CHAPTER_BATCH_SIZE)
                const batchInput = batch.map(({ q, i }) => ({
                    temp_idx: i,
                    question: stripHtml(q.question),
                    choices: Array.isArray(q.choices) ? q.choices.map((c: any) => stripHtml(String(c))) : []
                }))

                try {
                    const results = await gemini.assignChaptersBatch(batchInput, catalog)
                    updateQuestions(prev => {
                        const next = [...prev]
                        for (const item of results) {
                            const idx = item.temp_idx
                            if (idx == null || !next[idx]) continue
                            let found = catalog.find((c: any) => String(c.id) === String(item.chaporsubid) && !!c.is_subchapter === !!item.is_subchapter)
                            if (!found) found = catalog.find((c: any) => String(c.id) === String(item.chaporsubid))
                            if (found) {
                                const assignment: any = {
                                    chaporsubid: item.chaporsubid,
                                    is_subchapter: !!found.is_subchapter,
                                    chapter_id: found.is_subchapter ? (found.chapter_id || found.id) : found.id,
                                    chapter_name: found.chapter_name || found.name || `ID: ${item.chaporsubid}`,
                                    course_id: found.course_id ?? null,
                                }
                                if (found.is_subchapter) {
                                    assignment.subchapter_name = found.name || `Sub ID: ${item.chaporsubid}`
                                }

                                // Set visible fields
                                const visiblePatch: any = {}
                                if (found.is_subchapter) {
                                    visiblePatch.subchapter = String(item.chaporsubid)
                                    visiblePatch.chapter = String(assignment.chapter_id)
                                    if (useOptions) {
                                        visiblePatch.subchapter_option = Number(item.chaporsubid)
                                        visiblePatch.chapter_option = Number(assignment.chapter_id)
                                    }
                                } else {
                                    visiblePatch.chapter = String(item.chaporsubid)
                                    visiblePatch.subchapter = ''
                                    if (useOptions) {
                                        visiblePatch.chapter_option = Number(item.chaporsubid)
                                        visiblePatch.subchapter_option = undefined
                                    }
                                }

                                processedIndices.add(idx)
                                next[idx] = {
                                    ...next[idx],
                                    ...visiblePatch,
                                    _status: 'chapter_done',
                                    _course_id: assignment.course_id,
                                    _course_name: found.course_name || null,
                                    _chapterAssignment: assignment,
                                }
                                completed++
                            }
                        }
                        // Mark batch items that didn't get results
                        for (const { i } of batch) {
                            if (next[i]._status === 'pending') {
                                next[i] = { ...next[i], _retries: (next[i]._retries || 0) + 1 }
                            }
                        }
                        questionsRef.current = next
                        return next
                    })
                    setProgress(p => ({ ...p, completed, failed }))
                } catch (err: any) {
                    // Mark all in batch for retry
                    for (const { i } of batch) {
                        updateQuestion(i, { _retries: (questionsRef.current[i]?._retries || 0) + 1 })
                    }
                }
            }
        }

        // Mark remaining pending as failed
        updateQuestions(prev => {
            const next = [...prev]
            for (let i = 0; i < next.length; i++) {
                if (next[i]._status === 'pending') {
                    next[i] = { ...next[i], _status: 'failed', _failedPhase: 'chapters', _error: 'Chapter assignment failed after retries' }
                    failed++
                }
            }
            questionsRef.current = next
            return next
        })
        setProgress(p => ({ ...p, completed: p.total - failed, failed, message: 'Chapters assigned' }))

        return { catalog, parentChapters, subchapterMap }
    }, [token, gemini, updateQuestion, updateQuestions])

    // Phase C: RAG search
    const searchRag = useCallback(async (
        allRags: any[],
        selectedRagIds: string[]
    ) => {
        const chapterDone = questionsRef.current.filter(q => q._status === 'chapter_done')
        setProgress({ phase: 'rag', total: chapterDone.length, completed: 0, failed: 0, message: 'Searching textbooks...' })

        let completed = 0
        let failed = 0

        for (let retry = 0; retry < MAX_RETRIES; retry++) {
            if (cancelRef.current) return
            const pending = questionsRef.current
                .map((q, i) => ({ q, i }))
                .filter(({ q }) => q._status === 'chapter_done')

            if (pending.length === 0) break
            setProgress(p => ({ ...p, message: retry > 0 ? `Retry ${retry}/${MAX_RETRIES} for ${pending.length} questions...` : 'Searching textbooks...' }))

            for (const { q, i } of pending) {
                if (cancelRef.current) return
                try {
                    const rawChoices: string[] = Array.isArray(q.choices)
                        ? q.choices.map((c: any) => stripHtml(String(c || ''))).filter(Boolean)
                        : ['option1', 'option2', 'option3', 'option4'].map(f => q[f]).filter(Boolean).map((v: any) => stripHtml(String(v)))

                    const text = `${stripHtml(q.question)}\n\nChoices:\n${rawChoices.map((c, ci) => `${ci + 1}. ${c}`).join('\n')}`
                    const embedding = await gemini.getEmbedding(text, token)

                    // Determine target books based on course_id
                    const courseId = q._course_id
                    const courseName = (q as any)._course_name
                    let targetBookIds: string[] = selectedRagIds

                    if (courseId) {
                        // Match by api_course_id in associations (backend now reliably resolves this)
                        let matched = allRags.filter((b: any) => {
                            const assocs = b.associations || []
                            return assocs.some((a: any) => a.api_course_id != null && String(a.api_course_id) === String(courseId))
                        }).map((b: any) => String(b.id))

                        // Fallback: match by course_name
                        if (matched.length === 0 && courseName) {
                            matched = allRags.filter((b: any) => {
                                const assocs = b.associations || []
                                const courseNames: string[] = b.course_names || []
                                return assocs.some((a: any) => a.api_course_name && String(a.api_course_name).toLowerCase() === String(courseName).toLowerCase())
                                    || courseNames.some((cn: string) => cn.toLowerCase() === String(courseName).toLowerCase())
                            }).map((b: any) => String(b.id))
                        }

                        // Debug: show associations for each selected rag
                        allRags.forEach((b: any) => {
                            const assocs = (b.associations || []).map((a: any) => `{api_course_id:${a.api_course_id}, api_course_name:"${a.api_course_name}", chapter_id:${a.api_chapter_id}}`).join(', ')
                            })
                        if (matched.length > 0) targetBookIds = matched
                    }

                    const hasCourseBooks = targetBookIds !== selectedRagIds
                    const topK = hasCourseBooks ? targetBookIds.length * 5 : 15

                    const vsData = await request('/api/textbook/vector-search/', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
                        body: JSON.stringify({ book_ids: targetBookIds, embedding, top_k: topK })
                    })
                    const hits = (vsData?.results || [])
                        .sort((a: any, b: any) => (b.score ?? 0) - (a.score ?? 0))

                    // Build hits payload for Gemini
                    const hitsPayload = hits.map((h: any, hi: number) => {
                        const chapterName = h.chapter?.title || h.chapter?.name || ''
                        const chunkText = h.chunk && typeof h.chunk === 'object'
                            ? (h.chunk.text_content || h.chunk.text || JSON.stringify(h.chunk))
                            : String(h.chunk || '')
                        const bookEntry = allRags.find((b: any) => Number(b.id) === Number(h.book_id))
                        return {
                            rank: hi + 1,
                            score: h.score,
                            book_name: h.book_title ?? h.book?.title ?? h.book_name ?? null,
                            chapter: chapterName,
                            snippet: chunkText,
                            page_no: h.chunk?.page_no ?? null,
                        }
                    })

                    updateQuestion(i, { _status: 'rag_done', _ragHits: hitsPayload, _retries: 0 })
                    completed++
                    setProgress(p => ({ ...p, completed, failed }))
                } catch (err: any) {
                    updateQuestion(i, { _retries: (questionsRef.current[i]?._retries || 0) + 1 })
                    if ((questionsRef.current[i]?._retries || 0) >= MAX_RETRIES) {
                        updateQuestion(i, { _status: 'failed', _failedPhase: 'rag', _error: `RAG search failed: ${err?.message || err}` })
                        failed++
                        setProgress(p => ({ ...p, failed }))
                    }
                }
            }
        }

        // Mark remaining chapter_done as failed
        updateQuestions(prev => {
            const next = [...prev]
            for (let i = 0; i < next.length; i++) {
                if (next[i]._status === 'chapter_done') {
                    next[i] = { ...next[i], _status: 'failed', _failedPhase: 'rag', _error: 'RAG search failed after retries' }
                    failed++
                }
            }
            questionsRef.current = next
            return next
        })
        setProgress(p => ({ ...p, completed: chapterDone.length - failed, failed, message: 'RAG search complete' }))
    }, [token, gemini, updateQuestion, updateQuestions])

    // Phase D: Explanation generation
    const generateExplanations = useCallback(async (allRags: any[]) => {
        const ragDone = questionsRef.current.filter(q => q._status === 'rag_done')
        setProgress({ phase: 'explanation', total: ragDone.length, completed: 0, failed: 0, message: 'Generating explanations...' })

        const courseNames = Array.from(new Set(allRags.flatMap((b: any) => b.course_names || []))).filter(Boolean)
        let completed = 0
        let failed = 0
        const processedIndices = new Set<number>()

        for (let retry = 0; retry < MAX_RETRIES; retry++) {
            if (cancelRef.current) return
            const pending = questionsRef.current
                .map((q, i) => ({ q, i }))
                .filter(({ q, i }) => q._status === 'rag_done' && !processedIndices.has(i))

            if (pending.length === 0) break
            setProgress(p => ({ ...p, message: retry > 0 ? `Retry ${retry}/${MAX_RETRIES} for ${pending.length} questions...` : 'Generating explanations...' }))

            // Process in true batches — one AI call per batch of EXPLANATION_BATCH_SIZE
            for (let b = 0; b < pending.length; b += EXPLANATION_BATCH_SIZE) {
                if (cancelRef.current) return
                const batch = pending.slice(b, b + EXPLANATION_BATCH_SIZE)

                try {
                    // Build a single batch payload containing all questions in this batch
                    const batchPayload: Array<{
                        question_idx: number
                        question: string
                        choices: any[]
                        student_answer: string
                        hits: any[]
                    }> = []
                    const imageQuestions: Array<{ idx: number; file?: File; imageUrl?: string }> = []

                    for (const { q, i } of batch) {
                        const rawChoices: string[] = Array.isArray(q.choices)
                            ? q.choices.map((c: any) => stripHtml(String(c || ''))).filter(Boolean)
                            : []

                        batchPayload.push({
                            question_idx: i,
                            question: stripHtml(q.question),
                            choices: rawChoices.map((c, ci) => ({ label: String.fromCharCode(65 + ci), text: c })),
                            student_answer: q.answer ?? q.ans ?? q.correct_ans ?? '',
                            hits: q._ragHits || []
                        })

                        if (q.pendingImageFile || q.image) {
                            imageQuestions.push({
                                idx: i,
                                file: q.pendingImageFile as File | undefined,
                                imageUrl: typeof q.image === 'string' ? q.image : undefined
                            })
                        }
                    }

                    // Single AI call for the entire batch
                    const results = await gemini.generateExplanationsBatch(batchPayload, courseNames, imageQuestions)

                    updateQuestions(prev => {
                        const next = [...prev]
                        for (const result of results) {
                            const idx = result.question_idx
                            if (idx == null || !next[idx]) continue
                            if (result.insufficient_context) {
                                processedIndices.add(idx)
                                next[idx] = {
                                    ...next[idx],
                                    _status: 'no_context',
                                    explanation: result.explanation,
                                    references: result.references,
                                    corrected_answer: result.corrected_answer || undefined,
                                }
                                completed++
                            } else {
                                processedIndices.add(idx)
                                next[idx] = {
                                    ...next[idx],
                                    _status: 'explanation_done',
                                    explanation: result.explanation,
                                    references: result.references,
                                    corrected_answer: result.corrected_answer || undefined,
                                }
                                completed++
                            }
                        }
                        // Mark batch items that didn't get results for retry
                        for (const { i } of batch) {
                            if (next[i]._status === 'rag_done') {
                                next[i] = { ...next[i], _retries: (next[i]._retries || 0) + 1 }
                            }
                        }
                        questionsRef.current = next
                        return next
                    })
                    setProgress(p => ({ ...p, completed, failed }))
                } catch (err: any) {
                    for (const { i } of batch) {
                        updateQuestion(i, { _retries: (questionsRef.current[i]?._retries || 0) + 1 })
                    }
                }
            }
        }

        // Mark remaining rag_done as failed
        updateQuestions(prev => {
            const next = [...prev]
            for (let i = 0; i < next.length; i++) {
                if (next[i]._status === 'rag_done') {
                    next[i] = { ...next[i], _status: 'failed', _failedPhase: 'explanation', _error: 'Explanation generation failed after retries' }
                    failed++
                }
            }
            questionsRef.current = next
            return next
        })
        setProgress(p => ({ ...p, completed: ragDone.length - failed, failed, message: 'Explanations generated' }))
    }, [gemini, updateQuestion, updateQuestions])

    // Sort questions into succeeded / failed / no context and mark the run done.
    const finishRun = useCallback(() => {
        const final = questionsRef.current
        const succeeded: number[] = []
        const failedList: number[] = []
        const noContext: number[] = []
        final.forEach((q, i) => {
            if (q._status === 'explanation_done') succeeded.push(i)
            else if (q._status === 'no_context') noContext.push(i)
            else failedList.push(i) // failed, or anything left unfinished
        })
        setResults({ succeeded, failed: failedList, noContext })
        setProgress({ phase: 'done', total: final.length, completed: succeeded.length + noContext.length, failed: failedList.length, message: 'Processing complete' })
    }, [])

    // Main pipeline runner
    const runPipeline = useCallback(async (
        inputQuestions: any[],
        opts: RunOptions
    ) => {
        cancelRef.current = false
        lastRunOptsRef.current = opts

        // Initialize questions
        const pqs: PipelineQuestion[] = inputQuestions.map(q => {
            // Normalize choice_a/b/c/d into choices array if choices not already present
            let choices = q.choices
            if (!Array.isArray(choices) || choices.length === 0) {
                const fromFields = [q.choice_a, q.choice_b, q.choice_c, q.choice_d, q.choice_e].filter(Boolean)
                if (fromFields.length > 0) choices = fromFields
            }
            return {
                ...q,
                choices,
                _status: 'pending' as QuestionStatus,
                _retries: 0,
                _course_id: null,
                _ragHits: [],
                _error: undefined,
                _chapterAssignment: undefined,
            }
        })
        setQuestions(pqs)
        questionsRef.current = pqs
        setResults({ succeeded: [], failed: [], noContext: [] })

        try {
            // Phase B: Chapter assignment
            await assignChapters(pqs, opts.courseIds, opts.useOptions, opts.examId,
                opts.gradeIds && opts.gradeIds.length ? opts.gradeIds : opts.gradeId, opts.subjectId)
            if (cancelRef.current) return

            // Phase C: RAG search
            await searchRag(opts.allRags, opts.selectedRagIds)
            if (cancelRef.current) return

            // Phase D: Explanation generation
            await generateExplanations(opts.allRags)
            if (cancelRef.current) return

            finishRun()
        } catch (err: any) {
            setProgress(p => ({ ...p, phase: 'error', message: `Pipeline error: ${err?.message || err}` }))
        }
    }, [assignChapters, searchRag, generateExplanations, finishRun])

    const cancel = useCallback(() => {
        cancelRef.current = true
        setProgress(p => ({ ...p, phase: 'done', message: 'Cancelled' }))
    }, [])

    // Retry failed questions from the step each one stopped at.
    const retryFailed = useCallback(async (allRags: any[], selectedRagIds: string[]) => {
        cancelRef.current = false
        const opts = lastRunOptsRef.current
        // Back to the status the failed step starts from; a question whose step is unknown
        // restarts at chapter assignment (or explanation when it has no chapter step to redo).
        const restartAt = (q: PipelineQuestion): QuestionStatus => {
            if (q._failedPhase === 'explanation') return 'rag_done'
            if (q._failedPhase === 'rag') return 'chapter_done'
            if (q._failedPhase === 'chapters' || opts) return 'pending'
            return 'rag_done'
        }
        updateQuestions(prev => {
            const next = prev.map(q => q._status === 'failed'
                ? { ...q, _status: restartAt(q), _failedPhase: undefined, _retries: 0, _error: undefined }
                : q)
            questionsRef.current = next
            return next
        })
        try {
            if (questionsRef.current.some(q => q._status === 'pending') && opts) {
                await assignChapters(questionsRef.current, opts.courseIds, opts.useOptions, opts.examId,
                    opts.gradeIds && opts.gradeIds.length ? opts.gradeIds : opts.gradeId, opts.subjectId)
                if (cancelRef.current) return
            }
            if (questionsRef.current.some(q => q._status === 'chapter_done')) {
                await searchRag(allRags, selectedRagIds)
                if (cancelRef.current) return
            }
            if (questionsRef.current.some(q => q._status === 'rag_done')) {
                await generateExplanations(allRags)
                if (cancelRef.current) return
            }
            finishRun()
        } catch (err: any) {
            setProgress(p => ({ ...p, phase: 'error', message: `Retry error: ${err?.message || err}` }))
        }
    }, [updateQuestions, assignChapters, searchRag, generateExplanations, finishRun])

    // Helper: generate explanation for a single question inline (no main progress change)
    const generateExplanationForSingle = useCallback(async (index: number, allRags: any[], hits: any[]): Promise<boolean> => {
        const q = questionsRef.current[index]
        if (!q) return false

        const rawChoices: string[] = Array.isArray(q.choices)
            ? q.choices.map((c: any) => stripHtml(String(c || ''))).filter(Boolean)
            : []
        
        const batchPayload = [{
            question_idx: index,
            question: stripHtml(q.question),
            choices: rawChoices.map((c, ci) => ({ label: String.fromCharCode(65 + ci), text: c })),
            student_answer: q.answer ?? q.ans ?? q.correct_ans ?? '',
            hits: hits
        }]

        const imageQuestions = (q.pendingImageFile || q.image) ? [{
            idx: index,
            file: q.pendingImageFile as File | undefined,
            imageUrl: typeof q.image === 'string' ? q.image : undefined
        }] : []

        const courseNames = Array.from(new Set(allRags.flatMap((b: any) => b.course_names || []))).filter(Boolean)

        try {
            const results = await gemini.generateExplanationsBatch(batchPayload, courseNames, imageQuestions)
            let matched = false
            for (const result of results) {
                if (result.question_idx !== index) continue
                matched = true
                updateQuestion(index, {
                    _status: result.insufficient_context ? 'no_context' : 'explanation_done',
                    explanation: result.explanation,
                    references: result.references,
                    corrected_answer: result.corrected_answer || undefined,
                    _processing: false,
                    _isNewResult: true,
                })
            }
            if (!matched && results.length > 0) {
                const result = results[0]
                updateQuestion(index, {
                    _status: result.insufficient_context ? 'no_context' : 'explanation_done',
                    explanation: result.explanation,
                    references: result.references,
                    corrected_answer: result.corrected_answer || undefined,
                    _processing: false,
                    _isNewResult: true,
                })
                matched = true
            }
            return matched
        } catch (err) {
            updateQuestion(index, { _processing: false, _status: 'failed' })
            return false
        }
    }, [gemini, updateQuestion])

    // Helper: recompute results from current questions
    const recomputeResults = useCallback(() => {
        const final = questionsRef.current
        const succeeded: number[] = []
        const failedList: number[] = []
        const noContext: number[] = []
        final.forEach((q, idx) => {
            if (q._status === 'explanation_done') succeeded.push(idx)
            else if (q._status === 'no_context') noContext.push(idx)
            else if (q._status === 'failed') failedList.push(idx)
        })
        setResults({ succeeded, failed: failedList, noContext })
    }, [])

    // Retry no-context questions with all books
    const retryNoContextAllBooks = useCallback(async (indices: number[], allRags: any[]) => {
        cancelRef.current = false
        const allBookIds = allRags.map((b: any) => String(b.id))
        if (allBookIds.length === 0) {
            // Nothing to search: say so once instead of three failed searches per question.
            for (const i of indices) updateQuestion(i, { _processing: false, _error: 'No textbooks are linked to this exam\'s subject or courses.' })
            setProgress({ phase: 'idle', total: 0, completed: 0, failed: 0, message: '' })
            return
        }
        let completed = 0
        let failed = 0
        
        setProgress({ phase: 'rag', total: indices.length, completed: 0, failed: 0, message: 'Retrying with all books...' })

        for (const i of indices) {
            if (cancelRef.current) return
            const q = questionsRef.current[i]
            if (!q) continue

            updateQuestion(i, { _processing: true, _status: 'pending' })
            
            let success = false
            for (let r = 0; r < 3; r++) {
                if (cancelRef.current) return
                try {
                    const rawChoices: string[] = Array.isArray(q.choices)
                        ? q.choices.map((c: any) => stripHtml(String(c || ''))).filter(Boolean)
                        : []
                    const text = `${stripHtml(q.question)}\n\nChoices:\n${rawChoices.map((c, ci) => `${ci + 1}. ${c}`).join('\n')}`
                    const embedding = await gemini.getEmbedding(text, token)

                    const vsData = await request('/api/textbook/vector-search/', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
                        body: JSON.stringify({ book_ids: allBookIds, embedding, top_k: 50 })
                    })
                    const hits = (vsData?.results || [])
                        .sort((a: any, b: any) => (b.score ?? 0) - (a.score ?? 0))
                        .slice(0, 50)
                        .map((h: any, hi: number) => ({
                            rank: hi + 1,
                            score: h.score,
                            book_name: h.book_title ?? h.book?.title ?? h.book_name ?? null,
                            chapter: h.chapter?.title || h.chapter?.name || '',
                            snippet: h.chunk && typeof h.chunk === 'object' ? (h.chunk.text_content || h.chunk.text || JSON.stringify(h.chunk)) : String(h.chunk || ''),
                            page_no: h.chunk?.page_no ?? null,
                        }))

                    updateQuestion(i, { _ragHits: hits, _status: 'rag_done' })
                    success = await generateExplanationForSingle(i, allRags, hits)
                    if (success) break
                } catch (err: any) {
                    if (r === 2) updateQuestion(i, { _processing: false, _status: 'failed', _error: `Retry failed after 3 attempts: ${err?.message || err}` })
                }
            }
            
            if (success) completed++
            else failed++
            setProgress(p => ({ ...p, completed, failed }))
        }

        setProgress(p => ({ ...p, phase: 'done', message: 'Retry complete' }))
        recomputeResults()
    }, [token, gemini, updateQuestion, generateExplanationForSingle, recomputeResults])

    // Use AI knowledge for no-context questions
    const useAIKnowledge = useCallback(async (indices: number[]) => {
        let completed = 0
        let failed = 0
        setProgress({ phase: 'explanation', total: indices.length, completed: 0, failed: 0, message: 'Using AI knowledge...' })

        // Build a single batch payload for all questions
        const batchPayload: Array<{ question_idx: number; question: string; choices: any[]; student_answer: string }> = []
        for (const i of indices) {
            const q = questionsRef.current[i]
            if (!q) continue
            updateQuestion(i, { _processing: true, _status: 'pending' })
            const rawChoices: string[] = Array.isArray(q?.choices)
                ? q.choices.map((c: any) => stripHtml(String(c || ''))).filter(Boolean)
                : []
            batchPayload.push({
                question_idx: i,
                question: stripHtml(q?.question || ''),
                choices: rawChoices.map((c, ci) => ({ label: String.fromCharCode(65 + ci), text: c })),
                student_answer: q?.answer ?? q?.ans ?? q?.correct_ans ?? ''
            })
        }

        // Process in batches of EXPLANATION_BATCH_SIZE with retry
        for (let b = 0; b < batchPayload.length; b += EXPLANATION_BATCH_SIZE) {
            const chunk = batchPayload.slice(b, b + EXPLANATION_BATCH_SIZE)
            let success = false
            for (let r = 0; r < 3; r++) {
                try {
                    const results = await gemini.generateFromKnowledge(chunk)
                    for (const result of results) {
                        const idx = result.question_idx
                        if (idx == null || !questionsRef.current[idx]) continue
                        updateQuestion(idx, {
                            _status: 'explanation_done',
                            _processing: false,
                            explanation: result.explanation,
                            references: result.references,
                            corrected_answer: result.corrected_answer || undefined,
                        })
                        completed++
                    }
                    // Mark items that didn't get results as failed
                    const resultIdxSet = new Set(results.map((r: any) => r.question_idx))
                    for (const item of chunk) {
                        if (!resultIdxSet.has(item.question_idx)) {
                            if (r === 2) {
                                updateQuestion(item.question_idx, { _processing: false, _status: 'failed', _error: 'AI Knowledge: no result returned' })
                                failed++
                            }
                        }
                    }
                    success = true
                    break
                } catch (err) {
                    if (r === 2) {
                        for (const item of chunk) {
                            updateQuestion(item.question_idx, { _processing: false, _status: 'failed', _error: 'AI Knowledge failed after 3 attempts' })
                            failed++
                        }
                    }
                }
            }
            setProgress(p => ({ ...p, completed, failed }))
        }

        setProgress(p => ({ ...p, phase: 'done', message: 'AI Knowledge complete' }))
        recomputeResults()
    }, [gemini, updateQuestion, recomputeResults])

    // Retry with specific book IDs
    const retrySpecificBooks = useCallback(async (indices: number[], bookIds: string[], allRags: any[]) => {
        cancelRef.current = false
        let completed = 0
        let failed = 0
        setProgress({ phase: 'rag', total: indices.length, completed: 0, failed: 0, message: 'Searching specific books...' })

        for (const i of indices) {
            if (cancelRef.current) return
            const q = questionsRef.current[i]
            if (!q) continue

            updateQuestion(i, { _processing: true, _status: 'pending' })
            let success = false
            for (let r = 0; r < 3; r++) {
                if (cancelRef.current) return
                try {
                    const rawChoices: string[] = Array.isArray(q.choices)
                        ? q.choices.map((c: any) => stripHtml(String(c || ''))).filter(Boolean)
                        : []
                    const text = `${stripHtml(q.question)}\n\nChoices:\n${rawChoices.map((c, ci) => `${ci + 1}. ${c}`).join('\n')}`
                    const embedding = await gemini.getEmbedding(text, token)

                    const topK = bookIds.length * 10
                    const vsData = await request('/api/textbook/vector-search/', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
                        body: JSON.stringify({ book_ids: bookIds, embedding, top_k: topK })
                    })
                    const hits = (vsData?.results || [])
                        .sort((a: any, b: any) => (b.score ?? 0) - (a.score ?? 0))
                        .slice(0, 25)
                        .map((h: any, hi: number) => ({
                            rank: hi + 1,
                            score: h.score,
                            book_name: h.book_title ?? h.book?.title ?? h.book_name ?? null,
                            chapter: h.chapter?.title || h.chapter?.name || '',
                            snippet: h.chunk && typeof h.chunk === 'object' ? (h.chunk.text_content || h.chunk.text || JSON.stringify(h.chunk)) : String(h.chunk || ''),
                            page_no: h.chunk?.page_no ?? null,
                        }))

                    updateQuestion(i, { _ragHits: hits, _status: 'rag_done' })
                    success = await generateExplanationForSingle(i, allRags, hits)
                    if (success) break
                } catch (err: any) {
                    if (r === 2) updateQuestion(i, { _processing: false, _status: 'failed', _error: `Retry failed after 3 attempts: ${err?.message || err}` })
                }
            }
            if (success) completed++
            else failed++
            setProgress(p => ({ ...p, completed, failed }))
        }

        setProgress(p => ({ ...p, phase: 'done', message: 'Specific search complete' }))
        recomputeResults()
    }, [token, gemini, updateQuestion, generateExplanationForSingle, recomputeResults])

    // Reassign chapter for a single question then re-run RAG + explanation
    // Reassigning saves the chosen chapter on the question right away (it is a change, not a
    // suggestion), then refreshes the explanation from the textbooks when the exam has any.
    // Resolves true once the chapter is saved.
    const reassignChapter = useCallback(async (index: number, courseIds: string[], useOptions: boolean, allRags: any[], examId?: string | null): Promise<boolean> => {
        const q = questionsRef.current[index]
        if (!q) return false

        let saved = false
        updateQuestion(index, { _processing: true, _error: undefined })
        try {
            const catalogData = await request(`/api/school/exam-detail/${examId || ''}/assign-chapters-ai/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
                body: JSON.stringify({ use_options: !!useOptions, course_ids: courseIds })
            })
            const catalog = catalogData?.catalog || catalogData?.chapters || []

            const rawChoices: string[] = Array.isArray(q.choices)
                ? q.choices.map((c: any) => stripHtml(String(c || ''))).filter(Boolean)
                : []
            const batchInput = [{
                temp_idx: index,
                question: stripHtml(q.question || ''),
                choices: rawChoices
            }]

            const chapterResults = await gemini.assignChaptersBatch(batchInput, catalog)
            if (chapterResults.length > 0) {
                const cr = chapterResults[0]
                let found = catalog.find((c: any) => String(c.id) === String(cr.chaporsubid) && !!c.is_subchapter === !!cr.is_subchapter)
                if (!found) found = catalog.find((c: any) => String(c.id) === String(cr.chaporsubid))
                if (found) {
                    const assignment: any = {
                        chaporsubid: cr.chaporsubid,
                        is_subchapter: !!found.is_subchapter,
                        chapter_id: found.is_subchapter ? (found.chapter_id || found.id) : found.id,
                        chapter_name: found.chapter_name || found.name || `ID: ${cr.chaporsubid}`,
                        course_id: found.course_id ?? null,
                    }
                    if (found.is_subchapter) {
                        assignment.subchapter_name = found.name || `Sub ID: ${cr.chaporsubid}`
                    }
                    const subName = found.is_subchapter ? String(found.name || '').split(' > ').pop() || null : null
                    await request(`/api/school/exam-detail/${examId || ''}/add-change-chapter/`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
                        body: JSON.stringify(useOptions
                            ? { question: q.id, is_chapteroption: true, chapter_option: assignment.chapter_id, subchapter_option: found.is_subchapter ? found.id : null }
                            : { question: q.id, chapter: assignment.chapter_id, sub_chapter: found.is_subchapter ? found.id : null })
                    })
                    saved = true
                    updateQuestion(index, {
                        _chapterAssignment: assignment,
                        _course_id: found.course_id ?? null,
                        _course_name: found.course_name || null,
                        _status: 'chapter_done',
                        ...(useOptions
                            ? { is_chapteroption: true, chapter_option: assignment.chapter_id, chapter_option_name: assignment.chapter_name,
                                subchapter_option: found.is_subchapter ? found.id : null, subchapter_option_name: subName }
                            : { is_chapteroption: false, chapter: assignment.chapter_id, chapter_name: assignment.chapter_name, subchapter_name: subName }),
                    })
                }
            }
            if (!saved) throw new Error('no matching chapter')

            // No textbooks for this exam (a school subject exam has none linked by course): the chapter is the whole job.
            if (allRags.length === 0) {
                updateQuestion(index, { _processing: false })
                recomputeResults()
                return true
            }

            // Re-run RAG for this question
            const updatedQ = questionsRef.current[index]
            const courseId = updatedQ?._course_id
            const courseName = updatedQ?._course_name
            let targetBookIds: string[] = []

            if (courseId) {
                for (const rag of allRags) {
                    const assocs = rag.associations || []
                    const match = assocs.some((a: any) => String(a.api_course_id) === String(courseId))
                    const nameMatch = !match && courseName && (
                        (rag.course_names || []).some((cn: string) => cn.toLowerCase() === courseName.toLowerCase()) ||
                        assocs.some((a: any) => a.api_course_name?.toLowerCase() === courseName.toLowerCase())
                    )
                    if (match || nameMatch) targetBookIds.push(String(rag.id))
                }
            }
            if (targetBookIds.length === 0) targetBookIds = allRags.map((b: any) => String(b.id))

            const text = `${stripHtml(updatedQ?.question || '')}\n\nChoices:\n${(Array.isArray(updatedQ?.choices) ? updatedQ.choices : []).map((c: any, ci: number) => `${ci + 1}. ${stripHtml(String(c || ''))}`).join('\n')}`
            const embedding = await gemini.getEmbedding(text, token)
            const topK = Math.min(targetBookIds.length * 3, 25)
            const vsData = await request('/api/textbook/vector-search/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
                body: JSON.stringify({ book_ids: targetBookIds, embedding, top_k: topK })
            })
            const hits = (vsData?.results || [])
                .sort((a: any, b: any) => (b.score ?? 0) - (a.score ?? 0))
                .slice(0, 25)
                .map((h: any, hi: number) => ({
                    rank: hi + 1,
                    score: h.score,
                    book_name: h.book_title ?? h.book?.title ?? h.book_name ?? null,
                    chapter: h.chapter?.title || h.chapter?.name || '',
                    snippet: h.chunk && typeof h.chunk === 'object' ? (h.chunk.text_content || h.chunk.text || JSON.stringify(h.chunk)) : String(h.chunk || ''),
                    page_no: h.chunk?.page_no ?? null,
                }))

            updateQuestion(index, { _ragHits: hits, _status: 'rag_done' })
            await generateExplanationForSingle(index, allRags, hits)
        } catch (err) {
            updateQuestion(index, {
                _processing: false,
                _error: saved
                    ? `Chapter saved; the explanation could not be refreshed: ${(err as any)?.message || err}`
                    : `Reassign failed: ${(err as any)?.message || err}`,
            })
        }

        recomputeResults()
        return saved
    }, [token, gemini, updateQuestion, generateExplanationForSingle, recomputeResults])

    // Search by course: assign chapters for questions without one, then RAG search using course-specific books, in batches of 5
    const searchByCourse = useCallback(async (
        indices: number[],
        courseIds: string[],
        useOptions: boolean,
        allRags: any[],
        examId: string | null
    ) => {
        cancelRef.current = false
        const BATCH_SIZE = 5
        let completed = 0
        let failed = 0

        // Helper: check if a value is a meaningful assignment (not null/undefined/empty/sentinel -1)
        const isSet = (v: any) => v !== null && v !== undefined && v !== '' && String(v) !== '-1'

        setProgress({ phase: 'chapters', total: indices.length, completed: 0, failed: 0, message: 'Fetching chapter catalog...' })

        // Step 1: Fetch chapter catalog (needed for chapter assignment)
        let catalog: any[] = []
        try {
            const catalogData = await request(`/api/school/exam-detail/${examId || ''}/assign-chapters-ai/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
                body: JSON.stringify({ use_options: useOptions, course_ids: courseIds })
            })
            catalog = catalogData?.catalog || []
        } catch (err) {
            setProgress(p => ({ ...p, phase: 'error', message: 'Failed to fetch chapter catalog' }))
            return
        }

        console.log(`[SBC] catalog fetched: ${catalog.length} entries, use_options=${useOptions}, course_ids=${courseIds}`)
        console.log(`[SBC] allRags count=${allRags.length}`)
        allRags.forEach((b: any) => {
            const assocs = (b.associations || []).map((a: any) => `api_course_id=${a.api_course_id}`)
            console.log(`[SBC] rag book id=${b.id} title="${b.title}" associations=[${assocs.join(', ')}] course_names=${JSON.stringify(b.course_names || [])}`)
        })

        // Step 1b: Resolve course_id for questions that already have chapter_option_id / subchapter_option_id
        // The catalog is scoped to the exam's course_ids and may not contain chapter options from other courses.
        // So we call a dedicated backend endpoint that maps option IDs directly to their course.
        const allQs = indices.map(i => questionsRef.current[i]).filter(Boolean)
        const needsCourseResolution = allQs.filter(q =>
            !isSet(q._course_id) &&
            !q._chapterAssignment?.course_id &&
            (isSet(q.chapter_option_id) || isSet(q.subchapter_option_id) ||
             isSet(q.chapter_option) || isSet(q.subchapter_option))
        )

        // Build unique ID sets to batch the lookup
        const chapterOptionIds = Array.from(new Set(
            needsCourseResolution
                .map(q => q.chapter_option_id || q.chapter_option)
                .filter(v => isSet(v))
                .map(Number)
        ))
        const subchapterOptionIds = Array.from(new Set(
            needsCourseResolution
                .map(q => q.subchapter_option_id || q.subchapter_option)
                .filter(v => isSet(v))
                .map(Number)
        ))

        // courseByChapterOption[chapterOptionId] = { course_id, course_name }
        const courseByChapterOption: Record<number, { course_id: number; course_name: string | null }> = {}
        const courseBySub: Record<number, { course_id: number; course_name: string | null }> = {}

        if (chapterOptionIds.length > 0 || subchapterOptionIds.length > 0) {
            try {
                const qp = new URLSearchParams()
                if (chapterOptionIds.length) qp.set('ids', chapterOptionIds.join(','))
                if (subchapterOptionIds.length) qp.set('sub_ids', subchapterOptionIds.join(','))
                const lookupData = await request(`/api/chapter-options/course/?${qp.toString()}`, {
                    headers: { Authorization: token ? `Bearer ${token}` : '' },
                })
                const chOpts = lookupData?.chapter_options || {}
                const subOpts = lookupData?.subchapter_options || {}
                for (const [id, info] of Object.entries(chOpts)) {
                    courseByChapterOption[Number(id)] = info as any
                }
                for (const [id, info] of Object.entries(subOpts)) {
                    courseBySub[Number(id)] = info as any
                }
                console.log(`[SBC] course lookup: ch_option_ids=${chapterOptionIds} → ${JSON.stringify(courseByChapterOption)}`)
                console.log(`[SBC] course lookup: sub_option_ids=${subchapterOptionIds} → ${JSON.stringify(courseBySub)}`)
            } catch (err) {
                console.warn('[SBC] course lookup API failed', err)
            }
        }

        // Step 2: Identify ALL questions needing chapter assignment across the entire set
        const allNeedsChapter: { q: PipelineQuestion, i: number }[] = []
        for (const i of indices) {
            const q = questionsRef.current[i]
            if (!q) continue

            const hasExistingChapter =
                !!q._chapterAssignment ||
                isSet(q._course_id) ||
                isSet(q.chapter_id) ||
                isSet(q.chapter_name) ||
                isSet(q.chapter_option_id) ||
                isSet(q.chapter_option_name) ||
                isSet(q.chapter_option) ||
                isSet(q.subchapter_id) ||
                isSet(q.subchapter_name) ||
                isSet(q.subchapter_option_id) ||
                isSet(q.subchapter_option_name) ||
                isSet(q.subchapter_option);

            console.log(
                `[SBC] q[${i}] hasExisting=${hasExistingChapter}`,
                `_course_id=${q._course_id}`,
                `chapter_option_id=${q.chapter_option_id}`,
                `subchapter_option_id=${q.subchapter_option_id}`,
                `_chapterAssignment.course_id=${q._chapterAssignment?.course_id ?? 'none'}`
            )

            if (!hasExistingChapter) {
                allNeedsChapter.push({ q, i })
                updateQuestion(i, { _processing: true, _status: 'pending' })
                console.log(`[SBC] q[${i}] → no chapter, queued for assignment`)
            } else {
                // Resolve _course_id if not yet set
                if (!isSet(q._course_id)) {
                    // Priority 1: recover from _chapterAssignment (set by a previous pipeline step)
                    if (q._chapterAssignment?.course_id != null) {
                        updateQuestion(i, {
                            _course_id: q._chapterAssignment.course_id,
                            _course_name: (q as any)._course_name || null,
                        })
                        console.log(`[SBC] q[${i}] → _course_id=${q._chapterAssignment.course_id} (from _chapterAssignment)`)
                    } else {
                        // Priority 2: use the dedicated course-lookup result (most reliable for option IDs)
                        const subId = Number(q.subchapter_option_id || q.subchapter_option) || 0
                        const chId  = Number(q.chapter_option_id  || q.chapter_option)  || 0

                        const info = (subId && courseBySub[subId]) || (chId && courseByChapterOption[chId]) || null

                        if (info?.course_id) {
                            updateQuestion(i, {
                                _course_id: info.course_id,
                                _course_name: info.course_name || null,
                            })
                            console.log(`[SBC] q[${i}] → _course_id=${info.course_id} "${info.course_name}" (from option lookup)`)
                        } else {
                            // Priority 3: fall back to catalog scan (may succeed when option IDs are in scope)
                            const cid = q.chapter_id || q.chapter_option_id || q.chapter_option
                            const sid = q.subchapter_id || q.subchapter_option_id || q.subchapter_option
                            const found = catalog.find((c: any) =>
                                (isSet(sid) && String(c.id) === String(sid)) ||
                                (isSet(cid) && String(c.id) === String(cid)) ||
                                (isSet(cid) && String(c.chapter_id) === String(cid))
                            )
                            if (found?.course_id) {
                                updateQuestion(i, {
                                    _course_id: found.course_id,
                                    _course_name: found.course_name || null,
                                })
                                console.log(`[SBC] q[${i}] → _course_id=${found.course_id} (catalog fallback)`)
                            } else if (!isSet(cid) && !isSet(sid)) {
                                allNeedsChapter.push({ q, i })
                                updateQuestion(i, { _processing: true, _status: 'pending' })
                                console.log(`[SBC] q[${i}] → no numeric IDs, queued for re-assignment`)
                            } else {
                                console.warn(`[SBC] q[${i}] → could not resolve _course_id (cid=${cid} sid=${sid} info=${JSON.stringify(info)})`)
                            }
                        }
                    }
                } else {
                    console.log(`[SBC] q[${i}] → _course_id already set: ${q._course_id}`)
                }
            }
        }

        console.log(`[SBC] Step 2 done: ${allNeedsChapter.length} queued for assignment, ${indices.length - allNeedsChapter.length} already have chapters`)

        // Step 3: Perform mass chapter assignment if needed
        if (allNeedsChapter.length > 0 && catalog.length > 0) {
            setProgress(p => ({ ...p, phase: 'chapters', total: indices.length, completed: 0, failed: 0, message: `Assigning chapters for ${allNeedsChapter.length} questions...` }))
            
            // We can still batch the chapter assignment if it's too large, but for now let's do it in one go as requested, 
            // or use a larger batch size (e.g. 50) for Gemini.
            const CHAPTER_BATCH_SIZE = 50 
            for (let c = 0; c < allNeedsChapter.length; c += CHAPTER_BATCH_SIZE) {
                if (cancelRef.current) return
                const subBatch = allNeedsChapter.slice(c, c + CHAPTER_BATCH_SIZE)
                const batchInput = subBatch.map(({ q, i }) => ({
                    temp_idx: i,
                    question: stripHtml(q.question || ''),
                    choices: Array.isArray(q.choices) ? q.choices.map((c: any) => stripHtml(String(c || ''))).filter(Boolean) : []
                }))

                try {
                    const chapterResults = await gemini.assignChaptersBatch(batchInput, catalog)
                    for (const item of chapterResults) {
                        const idx = item.temp_idx
                        if (idx == null || !questionsRef.current[idx]) continue
                        let found = catalog.find((c: any) => String(c.id) === String(item.chaporsubid) && !!c.is_subchapter === !!item.is_subchapter)
                        if (!found) found = catalog.find((c: any) => String(c.id) === String(item.chaporsubid))
                        if (found) {
                            const assignment: any = {
                                chaporsubid: item.chaporsubid,
                                is_subchapter: !!found.is_subchapter,
                                chapter_id: found.is_subchapter ? (found.chapter_id || found.id) : found.id,
                                chapter_name: found.chapter_name || found.name || `ID: ${item.chaporsubid}`,
                                course_id: found.course_id ?? null,
                            }
                            if (found.is_subchapter) {
                                assignment.subchapter_name = found.name || `Sub ID: ${item.chaporsubid}`
                            }
                            console.log(`[SBC] q[${idx}] AI assigned chaporsubid=${item.chaporsubid} → course_id=${found.course_id} "${found.course_name}"`)
                            updateQuestion(idx, {
                                _chapterAssignment: assignment,
                                _course_id: found.course_id ?? null,
                                _course_name: found.course_name || null,
                                _status: 'chapter_done',
                            })
                        } else {
                            console.warn(`[SBC] q[${idx}] AI returned chaporsubid=${item.chaporsubid} but not found in catalog`)
                        }
                    }
                } catch (err: any) {
                    for (const { i } of subBatch) {
                        if (!questionsRef.current[i]?._chapterAssignment) {
                            updateQuestion(i, { _processing: false, _status: 'failed', _error: 'Chapter assignment failed' })
                        }
                    }
                }
            }
        }

        // Step 4: Batched RAG search and Explanation Generation
        for (let b = 0; b < indices.length; b += BATCH_SIZE) {
            if (cancelRef.current) return
            const batch = indices.slice(b, b + BATCH_SIZE)
            const batchNum = Math.floor(b / BATCH_SIZE) + 1

            // Mark RAG phase processing
            for (const i of batch) {
                const q = questionsRef.current[i]
                if (q && q._status !== 'failed') {
                    updateQuestion(i, { _processing: true })
                }
            }

            setProgress(p => ({ ...p, phase: 'rag', message: `Searching and generating explanations (batch ${batchNum})...` }))

            for (const i of batch) {
                if (cancelRef.current) return
                const q = questionsRef.current[i]
                if (!q || q._status === 'failed') continue

                try {
                    // Find course-specific books
                    const courseId = q._course_id
                    const courseName = (q as any)._course_name
                    let targetBookIds: string[] = []

                    console.log(`[SBC] q[${i}] RAG phase: _course_id=${courseId} _course_name="${courseName}" allRags.count=${allRags.length}`)

                    if (courseId) {
                        for (const rag of allRags) {
                            const assocs = rag.associations || []
                            // 1. Direct ID match
                            const idMatch = assocs.some((a: any) => isSet(a.api_course_id) && String(a.api_course_id) === String(courseId))

                            // 2. Name match
                            const nameMatch = courseName && (
                                (rag.course_names || []).some((cn: any) => isSet(cn) && String(cn).toLowerCase() === String(courseName).toLowerCase()) ||
                                assocs.some((a: any) => isSet(a.api_course_name) && String(a.api_course_name).toLowerCase() === String(courseName).toLowerCase())
                            )

                            const ragAssocSummary = assocs.map((a: any) => `api_course_id=${a.api_course_id}`).join(', ')
                            console.log(`[SBC] q[${i}]   book id=${rag.id} "${rag.title}" idMatch=${idMatch} nameMatch=${nameMatch} assocs=[${ragAssocSummary}]`)

                            if (idMatch || nameMatch) {
                                targetBookIds.push(String(rag.id))
                            }
                        }
                        console.log(`[SBC] q[${i}] matched books for course ${courseId}: [${targetBookIds.join(', ')}]`)
                    }

                    // If we have no specific matches, fall back to all available rags for this exam context.
                    if (targetBookIds.length === 0) {
                        targetBookIds = allRags.map((rb: any) => String(rb.id))
                        console.warn(`[SBC] q[${i}] no course-specific books found for course_id=${courseId}, falling back to all ${targetBookIds.length} books`)
                    }

                    const rawChoices: string[] = Array.isArray(q.choices)
                        ? q.choices.map((c: any) => stripHtml(String(c || ''))).filter(Boolean)
                        : []
                    const text = `${stripHtml(q.question || '')}\n\nChoices:\n${rawChoices.map((c, ci) => `${ci + 1}. ${c}`).join('\n')}`
                    const embedding = await gemini.getEmbedding(text, token)
                    const topK = Math.min(targetBookIds.length * 5, 25)

                    const vsData = await request('/api/textbook/vector-search/', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
                        body: JSON.stringify({ book_ids: targetBookIds, embedding, top_k: topK })
                    })
                    const hits = (vsData?.results || [])
                        .sort((a: any, b: any) => (b.score ?? 0) - (a.score ?? 0))
                        .slice(0, 25)
                        .map((h: any, hi: number) => ({
                            rank: hi + 1,
                            score: h.score,
                            book_name: h.book_title ?? h.book?.title ?? h.book_name ?? null,
                            chapter: h.chapter?.title || h.chapter?.name || '',
                            snippet: h.chunk && typeof h.chunk === 'object' ? (h.chunk.text_content || h.chunk.text || JSON.stringify(h.chunk)) : String(h.chunk || ''),
                            page_no: h.chunk?.page_no ?? null,
                        }))

                    updateQuestion(i, { _ragHits: hits, _status: 'rag_done' })
                } catch (err: any) {
                    updateQuestion(i, { _processing: false, _status: 'failed', _error: `RAG search failed: ${err?.message || err}` })
                    failed++
                }
            }

            // Step 4: Generate explanations for this batch
            setProgress(p => ({ ...p, phase: 'explanation', message: `Generating explanations (batch ${batchNum})...` }))

            const ragDoneInBatch = batch.filter(i => questionsRef.current[i]?._status === 'rag_done')
            if (ragDoneInBatch.length > 0) {
                const batchPayload = ragDoneInBatch.map(i => {
                    const q2 = questionsRef.current[i]
                    const rawChoices2: string[] = Array.isArray(q2?.choices)
                        ? q2.choices.map((c: any) => stripHtml(String(c || ''))).filter(Boolean)
                        : []
                    return {
                        question_idx: i,
                        question: stripHtml(q2?.question || ''),
                        choices: rawChoices2.map((c, ci) => ({ label: String.fromCharCode(65 + ci), text: c })),
                        student_answer: q2?.answer ?? q2?.ans ?? q2?.correct_ans ?? '',
                        hits: q2?._ragHits || []
                    }
                })

                try {
                    const courseNames = Array.from(new Set(allRags.flatMap((rb: any) => rb.course_names || []))).filter(Boolean)
                    const results = await gemini.generateExplanationsBatch(batchPayload, courseNames, [])

                    const resultIdxSet = new Set<number>()
                    for (const result of results) {
                        const idx = result.question_idx
                        if (idx == null || !questionsRef.current[idx]) continue
                        resultIdxSet.add(idx)
                        updateQuestion(idx, {
                            _status: result.insufficient_context ? 'no_context' : 'explanation_done',
                            explanation: result.explanation,
                            references: result.references,
                            corrected_answer: result.corrected_answer || undefined,
                            _processing: false,
                            _isNewResult: true,
                        })
                        completed++
                    }
                    // Mark items without results as failed
                    for (const i of ragDoneInBatch) {
                        if (!resultIdxSet.has(i) && questionsRef.current[i]?._status === 'rag_done') {
                            updateQuestion(i, { _processing: false, _status: 'failed', _error: 'No explanation generated' })
                            failed++
                        }
                    }
                } catch (err: any) {
                    for (const i of ragDoneInBatch) {
                        updateQuestion(i, { _processing: false, _status: 'failed', _error: `Explanation failed: ${err?.message || err}` })
                        failed++
                    }
                }
            }

            setProgress(p => ({ ...p, completed, failed }))
        }

        setProgress(p => ({ ...p, phase: 'done', message: 'Search by course complete' }))
        recomputeResults()
    }, [token, gemini, updateQuestion, recomputeResults])

    // AI-Assisted Generation:
    // 1. In batches of 20, ask Gemini to pick the top 3 most relevant books per question
    //    from all books available across the exam's courses.
    // 2. For each question, run vector search on those 3 books (3 hits per book = 9 hits).
    // 3. Generate explanations in batches of 5 using the standard explanations endpoint.
    const aiAssistedGeneration = useCallback(async (
        indices: number[],
        allRags: any[]
    ) => {
        cancelRef.current = false
        const BOOK_SELECTION_BATCH = 20
        const EXPLANATION_BATCH = 5
        const HITS_PER_BOOK = 3
        const BOOKS_PER_QUESTION = 3
        let completed = 0
        let failed = 0

        if (!allRags || allRags.length === 0) {
            setProgress({ phase: 'error', total: indices.length, completed: 0, failed: 0, message: 'No books available' })
            return
        }

        // Build book catalog for the AI selector
        const bookCatalog = allRags.map((b: any) => ({
            id: Number(b.id),
            title: String(b.title || ''),
            author: b.author || null,
            course_names: Array.isArray(b.course_names) ? b.course_names : [],
            subject: b.subject || null,
            grade: b.grade || null,
        }))

        console.log(`[AI-Gen] starting: ${indices.length} questions, ${bookCatalog.length} books in catalog`)

        // Mark all selected questions as processing up-front so the UI shows the spinner
        for (const i of indices) {
            const q = questionsRef.current[i]
            if (q) updateQuestion(i, { _processing: true, _status: 'pending' })
        }

        // ---------- Phase 1: AI book selection (batches of 20) ----------
        setProgress({ phase: 'rag', total: indices.length, completed: 0, failed: 0, message: 'AI selecting relevant books...' })

        const bookSelectionByIdx: Record<number, number[]> = {}
        for (let b = 0; b < indices.length; b += BOOK_SELECTION_BATCH) {
            if (cancelRef.current) return
            const batch = indices.slice(b, b + BOOK_SELECTION_BATCH)
            const batchPayload = batch
                .map(i => {
                    const q = questionsRef.current[i]
                    if (!q) return null
                    const rawChoices: string[] = Array.isArray(q.choices)
                        ? q.choices.map((c: any) => stripHtml(String(c || ''))).filter(Boolean)
                        : []
                    return {
                        question_idx: i,
                        question: stripHtml(q.question || ''),
                        choices: rawChoices,
                    }
                })
                .filter(Boolean) as Array<{ question_idx: number; question: string; choices: string[] }>

            try {
                const selectionResults = await gemini.selectBooksBatch(batchPayload, bookCatalog)
                for (const r of selectionResults) {
                    if (Array.isArray(r.book_ids) && r.book_ids.length > 0) {
                        bookSelectionByIdx[r.question_idx] = r.book_ids.slice(0, BOOKS_PER_QUESTION)
                    }
                }
                console.log(`[AI-Gen] book selection batch ${Math.floor(b / BOOK_SELECTION_BATCH) + 1} done: ${selectionResults.length} results`)
            } catch (err: any) {
                console.warn(`[AI-Gen] book selection batch failed`, err)
                // Don't fail outright — questions in this batch will fall back to the first 3 books
            }
        }

        // ---------- Phase 2: vector search on the AI-selected books ----------
        setProgress(p => ({ ...p, message: 'Searching textbooks...' }))

        const fallbackBookIds = allRags.slice(0, BOOKS_PER_QUESTION).map((b: any) => Number(b.id))

        for (const i of indices) {
            if (cancelRef.current) return
            const q = questionsRef.current[i]
            if (!q) continue

            const selectedIds = bookSelectionByIdx[i] && bookSelectionByIdx[i].length > 0
                ? bookSelectionByIdx[i]
                : fallbackBookIds

            const targetBookIds = selectedIds.map(String)
            console.log(`[AI-Gen] q[${i}] selected books=[${targetBookIds.join(', ')}]`)

            try {
                const rawChoices: string[] = Array.isArray(q.choices)
                    ? q.choices.map((c: any) => stripHtml(String(c || ''))).filter(Boolean)
                    : []
                const text = `${stripHtml(q.question || '')}\n\nChoices:\n${rawChoices.map((c, ci) => `${ci + 1}. ${c}`).join('\n')}`
                const embedding = await gemini.getEmbedding(text, token)

                const totalK = HITS_PER_BOOK * targetBookIds.length
                const vsData = await request('/api/textbook/vector-search/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
                    body: JSON.stringify({
                        book_ids: targetBookIds,
                        embedding,
                        top_k: totalK,
                        per_book_k: HITS_PER_BOOK,
                    }),
                })
                const hits = (vsData?.results || [])
                    .sort((a: any, b: any) => (b.score ?? 0) - (a.score ?? 0))
                    .slice(0, totalK)
                    .map((h: any, hi: number) => ({
                        rank: hi + 1,
                        score: h.score,
                        book_name: h.book_title ?? h.book?.title ?? h.book_name ?? null,
                        chapter: h.chapter?.title || h.chapter?.name || '',
                        snippet: h.chunk && typeof h.chunk === 'object' ? (h.chunk.text_content || h.chunk.text || JSON.stringify(h.chunk)) : String(h.chunk || ''),
                        page_no: h.chunk?.page_no ?? null,
                    }))

                updateQuestion(i, { _ragHits: hits, _status: 'rag_done' })
            } catch (err: any) {
                updateQuestion(i, { _processing: false, _status: 'failed', _error: `RAG search failed: ${err?.message || err}` })
                failed++
            }
        }

        // ---------- Phase 3: explanation generation (batches of 5) ----------
        setProgress(p => ({ ...p, phase: 'explanation', message: 'Generating explanations...' }))

        const courseNames = Array.from(new Set(allRags.flatMap((rb: any) => rb.course_names || []))).filter(Boolean)
        const ragDoneIndices = indices.filter(i => questionsRef.current[i]?._status === 'rag_done')

        for (let b = 0; b < ragDoneIndices.length; b += EXPLANATION_BATCH) {
            if (cancelRef.current) return
            const batch = ragDoneIndices.slice(b, b + EXPLANATION_BATCH)

            const batchPayload = batch.map(i => {
                const q2 = questionsRef.current[i]
                const rawChoices2: string[] = Array.isArray(q2?.choices)
                    ? q2.choices.map((c: any) => stripHtml(String(c || ''))).filter(Boolean)
                    : []
                return {
                    question_idx: i,
                    question: stripHtml(q2?.question || ''),
                    choices: rawChoices2.map((c, ci) => ({ label: String.fromCharCode(65 + ci), text: c })),
                    student_answer: q2?.answer ?? q2?.ans ?? q2?.correct_ans ?? '',
                    hits: q2?._ragHits || [],
                }
            })

            try {
                const results = await gemini.generateExplanationsBatch(batchPayload, courseNames, [])
                const resultIdxSet = new Set<number>()
                for (const result of results) {
                    const idx = result.question_idx
                    if (idx == null || !questionsRef.current[idx]) continue
                    resultIdxSet.add(idx)
                    updateQuestion(idx, {
                        _status: result.insufficient_context ? 'no_context' : 'explanation_done',
                        explanation: result.explanation,
                        references: result.references,
                        corrected_answer: result.corrected_answer || undefined,
                        _processing: false,
                        _isNewResult: true,
                    })
                    completed++
                }
                for (const i of batch) {
                    if (!resultIdxSet.has(i) && questionsRef.current[i]?._status === 'rag_done') {
                        updateQuestion(i, { _processing: false, _status: 'failed', _error: 'No explanation generated' })
                        failed++
                    }
                }
            } catch (err: any) {
                for (const i of batch) {
                    updateQuestion(i, { _processing: false, _status: 'failed', _error: `Explanation failed: ${err?.message || err}` })
                    failed++
                }
            }
            setProgress(p => ({ ...p, completed, failed }))
        }

        setProgress(p => ({ ...p, phase: 'done', message: 'AI Assisted Generation complete' }))
        recomputeResults()
    }, [token, gemini, updateQuestion, recomputeResults])

    // Skip RAG only: initialize questions, run chapter assignment, then AI-knowledge explanations.
    // Used when no textbooks are available (e.g. an "open" national exam with
    // requires_institution=false AND requires_department=false). Chapters come from a separate
    // catalog endpoint that does not depend on textbooks, so chapter assignment still runs.
    const runAIKnowledgeOnly = useCallback(async (
        inputQuestions: any[],
        opts: {
            courseIds: string[]
            examId: string | null
            useOptions: boolean
            gradeId?: string
            subjectId?: string
        }
    ) => {
        cancelRef.current = false

        // Same normalization as runPipeline (see lines 493-512).
        const pqs: PipelineQuestion[] = inputQuestions.map(q => {
            let choices = q.choices
            if (!Array.isArray(choices) || choices.length === 0) {
                const fromFields = [q.choice_a, q.choice_b, q.choice_c, q.choice_d, q.choice_e].filter(Boolean)
                if (fromFields.length > 0) choices = fromFields
            }
            return {
                ...q,
                choices,
                _status: 'pending' as QuestionStatus,
                _retries: 0,
                _course_id: null,
                _ragHits: [],
                _error: undefined,
                _chapterAssignment: undefined,
            }
        })
        setQuestions(pqs)
        questionsRef.current = pqs
        setResults({ succeeded: [], failed: [], noContext: [] })

        if (pqs.length === 0) {
            setProgress({ phase: 'done', total: 0, completed: 0, failed: 0, message: 'No questions to process' })
            return
        }

        try {
            // Phase B: Chapter assignment (independent of textbooks)
            await assignChapters(pqs, opts.courseIds, opts.useOptions, opts.examId, opts.gradeId, opts.subjectId)
            if (cancelRef.current) return

            // Phase D: AI-knowledge explanations (skipping Phase C RAG entirely)
            const allIndices = questionsRef.current.map((_, i) => i)
            await useAIKnowledge(allIndices)
        } catch (err: any) {
            setProgress(p => ({ ...p, phase: 'error', message: `Pipeline error: ${err?.message || err}` }))
        }
    }, [assignChapters, useAIKnowledge])

    // Manual edits after processing: the teacher's word overrides the AI's.
    const editQuestionMeta = useCallback((idx: number, patch: { explanation?: string; references?: string[] }) => {
        updateQuestion(idx, patch)
    }, [updateQuestion])

    const setQuestionChapter = useCallback((idx: number, entry: any, useOptions: boolean) => {
        if (!entry) return
        const isSub = !!entry.is_subchapter
        const chapterId = isSub ? (entry.chapter_id || entry.id) : entry.id
        const assignment: any = {
            chaporsubid: entry.id,
            is_subchapter: isSub,
            chapter_id: chapterId,
            chapter_name: isSub ? (entry.chapter_name || `ID: ${chapterId}`) : (entry.chapter_name || entry.name),
            course_id: entry.course_id ?? null,
        }
        // Catalog subchapter names read "Chapter > Subchapter".
        if (isSub) assignment.subchapter_name = String(entry.name).split(' > ').pop()
        const patch: any = isSub
            ? { subchapter: String(entry.id), chapter: String(chapterId) }
            : { chapter: String(entry.id), subchapter: '' }
        if (useOptions) {
            patch.chapter_option = Number(chapterId)
            patch.subchapter_option = isSub ? Number(entry.id) : undefined
        }
        updateQuestion(idx, { ...patch, _chapterAssignment: assignment, _course_id: assignment.course_id })
    }, [updateQuestion])

    return {
        chapterCatalog,
        editQuestionMeta,
        setQuestionChapter,
        questions,
        progress,
        results,
        detectChapterType,
        runPipeline,
        runAIKnowledgeOnly,
        cancel,
        retryFailed,
        retryNoContextAllBooks,
        retrySpecificBooks,
        useAIKnowledge,
        reassignChapter,
        searchByCourse,
        aiAssistedGeneration,
        updateQuestion,
        setQuestions: setQuestionsCustom,
    }
}
